import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Row,
  Col,
  Card,
  Table,
  Badge,
  Button,
  Spinner,
  Alert,
  Form,
} from 'react-bootstrap';
import { useSearchParams, Link } from 'react-router-dom';
import { sickLeaveApplicationsApiService } from '../../services/apiServices';
import { SickLeaveApplicationResponse, SickLeaveApplicationStatus } from '../../api-client';
import {
  getSickLeaveApplicationStatusLabel,
  getSickLeaveApplicationStatusBadgeVariant,
} from '../utils/statusConverters';
import { SmartPagination } from '../../components/SmartPagination';

const LIMIT = 10;

const MySickLeavePage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [applications, setApplications] = useState<SickLeaveApplicationResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [createSuccess, setCreateSuccess] = useState(false);

  const currentPage = parseInt(searchParams.get('page') || '1', 10);
  const statusFilter = searchParams.get('status') || '';

  const loadApplications = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const offset = (currentPage - 1) * LIMIT;
      const response = await sickLeaveApplicationsApiService.getMyApplications(
        (statusFilter as SickLeaveApplicationStatus) || undefined,
        LIMIT,
        offset
      );
      setApplications(response.data || []);
    } catch (err: any) {
      setError(err.message || 'Ошибка при загрузке заявок');
    } finally {
      setLoading(false);
    }
  }, [currentPage, statusFilter]);

  useEffect(() => {
    loadApplications();
  }, [loadApplications]);

  const handleStatusFilterChange = (value: string) => {
    const params = new URLSearchParams(searchParams);
    if (value) {
      params.set('status', value);
    } else {
      params.delete('status');
    }
    params.delete('page');
    setSearchParams(params);
  };

  const handleResetFilters = () => {
    setSearchParams({});
  };

  const handlePageChange = (page: number) => {
    const params = new URLSearchParams(searchParams);
    params.set('page', page.toString());
    setSearchParams(params);
  };

  const handleCreateSuccess = () => {
    setShowCreateForm(false);
    setCreateSuccess(true);
    loadApplications();
    setTimeout(() => setCreateSuccess(false), 3000);
  };

  return (
    <Container fluid>
      <Row className="mt-4">
        <Col>
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h2>Мои больничные</h2>
            <Button variant="primary" onClick={() => setShowCreateForm(!showCreateForm)}>
              {showCreateForm ? 'Скрыть форму' : 'Новый больничный'}
            </Button>
          </div>

          {createSuccess && <Alert variant="success">Заявка успешно создана!</Alert>}

          {showCreateForm && (
            <Card className="mb-4">
              <Card.Header>Новая заявка на больничный</Card.Header>
              <Card.Body>
                <SickLeaveCreateForm
                  onSuccess={handleCreateSuccess}
                  onCancel={() => setShowCreateForm(false)}
                />
              </Card.Body>
            </Card>
          )}

          <Card>
            <Card.Header>
              <div className="d-flex gap-2 align-items-center">
                <Form.Select
                  size="sm"
                  style={{ width: 'auto' }}
                  value={statusFilter}
                  onChange={(e) => handleStatusFilterChange(e.target.value)}
                >
                  <option value="">Все статусы</option>
                  <option value="pending">Ожидает обработки</option>
                  <option value="active">Активен</option>
                  <option value="completed">Завершен</option>
                  <option value="canceled">Отменен</option>
                </Form.Select>
                <Button variant="outline-secondary" size="sm" onClick={handleResetFilters}>
                  Сбросить
                </Button>
              </div>
            </Card.Header>
            <Card.Body>
              {loading ? (
                <div className="text-center py-4">
                  <Spinner animation="border" />
                </div>
              ) : error ? (
                <Alert variant="danger">{error}</Alert>
              ) : applications.length === 0 ? (
                <p className="text-muted text-center py-4">Заявки не найдены</p>
              ) : (
                <>
                  <Table striped bordered hover responsive>
                    <thead>
                      <tr>
                        <th>Дата начала</th>
                        <th>Дата окончания</th>
                        <th>Статус</th>
                        <th>Дата создания</th>
                        <th>Действия</th>
                      </tr>
                    </thead>
                    <tbody>
                      {applications.map((app) => (
                        <tr key={app.id}>
                          <td>{new Date(app.start_date).toLocaleDateString()}</td>
                          <td>{new Date(app.end_date).toLocaleDateString()}</td>
                          <td>
                            <Badge bg={getSickLeaveApplicationStatusBadgeVariant(app.status)}>
                              {getSickLeaveApplicationStatusLabel(app.status)}
                            </Badge>
                          </td>
                          <td>{new Date(app.created_at).toLocaleString()}</td>
                          <td>
                            <Link to={`/sick-leave/my/${app.id}`}>
                              <Button variant="outline-primary" size="sm">
                                Подробнее
                              </Button>
                            </Link>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                  <SmartPagination
                    currentPage={currentPage}
                    limit={LIMIT}
                    currentItemsCount={applications.length}
                    onPageChange={handlePageChange}
                  />
                </>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

interface SickLeaveCreateFormProps {
  onSuccess: () => void;
  onCancel: () => void;
}

const SickLeaveCreateForm: React.FC<SickLeaveCreateFormProps> = ({ onSuccess, onCancel }) => {
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!startDate || !endDate) {
      setError('Пожалуйста, укажите даты начала и окончания');
      return;
    }

    try {
      setSubmitting(true);
      setError(null);
      await sickLeaveApplicationsApiService.createMyApplication(startDate, endDate);
      onSuccess();
    } catch (err: any) {
      setError(err.message || 'Ошибка при создании заявки');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Form onSubmit={handleSubmit}>
      {error && <Alert variant="danger">{error}</Alert>}

      <Row>
        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>Дата начала</Form.Label>
            <Form.Control
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              disabled={submitting}
              style={{ width: 'auto' }}
              required
            />
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>Дата окончания</Form.Label>
            <Form.Control
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              disabled={submitting}
              min={startDate || undefined}
              style={{ width: 'auto' }}
              required
            />
          </Form.Group>
        </Col>
      </Row>

      <div className="d-flex gap-2">
        <Button variant="primary" type="submit" disabled={submitting}>
          {submitting ? (
            <>
              <Spinner animation="border" size="sm" className="me-1" />
              Создание...
            </>
          ) : (
            'Создать заявку'
          )}
        </Button>
        <Button variant="outline-secondary" onClick={onCancel} disabled={submitting}>
          Отмена
        </Button>
      </div>
    </Form>
  );
};

export default MySickLeavePage;
